import React from 'react';
import { Car } from 'lucide-react';
import { SystemPlanningConfig, EVType, EV_TYPE_LABELS, EV_CHARGING_RATES } from '../../types/systemPlanning';

interface Props {
  config: SystemPlanningConfig;
  updateConfig: (partial: Partial<SystemPlanningConfig>) => void;
  updateEVType: (index: number, type: EVType) => void;
}

export function EVConfigSection({ config, updateConfig, updateEVType }: Props) {
  const totalEVKw = config.evConfigs
    .slice(0, config.evCount)
    .reduce((sum, ev) => sum + EV_CHARGING_RATES[ev.type], 0);

  return (
    <div>
      <div className="flex items-center justify-between mb-3">
        <label className="flex items-center gap-2 font-medium text-gray-700">
          <Car size={16} className="text-green-600" />
          Electric Vehicles
        </label>
        {config.evCount > 0 && (
          <span className="text-sm font-semibold text-green-700 bg-green-50 px-2 py-0.5 rounded">
            {totalEVKw.toFixed(1)} kW total
          </span>
        )}
      </div>

      <div className="flex items-center gap-3 mb-3">
        <span className="text-sm text-gray-500 w-6">0</span>
        <input
          type="range"
          min={0}
          max={3}
          step={1}
          value={config.evCount}
          onChange={e => updateConfig({ evCount: Number(e.target.value) as 0 | 1 | 2 | 3 })}
          className="flex-1 accent-green-600"
        />
        <span className="text-sm text-gray-500 w-6">3</span>
        <span className="text-sm font-bold text-gray-700 w-16 text-right">
          {config.evCount} EV{config.evCount !== 1 ? 's' : ''}
        </span>
      </div>

      {config.evCount > 0 && (
        <div className="space-y-2">
          {Array.from({ length: config.evCount }).map((_, i) => (
            <div key={i} className="flex items-center gap-2">
              <span className="text-xs text-gray-500 w-12">EV {i + 1}</span>
              <select
                value={config.evConfigs[i]?.type ?? 'economy'}
                onChange={e => updateEVType(i, e.target.value as EVType)}
                className="flex-1 text-sm border border-gray-200 rounded px-2 py-1 bg-white focus:outline-none focus:ring-1 focus:ring-green-500"
              >
                {(Object.keys(EV_TYPE_LABELS) as EVType[]).map(type => (
                  <option key={type} value={type}>{EV_TYPE_LABELS[type]}</option>
                ))}
              </select>
            </div>
          ))}
        </div>
      )}

      {config.evCount === 0 && (
        <p className="text-xs text-gray-400 italic">No EVs configured</p>
      )}
    </div>
  );
}
